// Instagram Feed Component with Embed Fallback
// Uses Instagram embeds when API is not available

import React, { useState, useEffect } from "react";
import {
  Instagram,
  ExternalLink,
  Heart,
  MessageCircle,
  Calendar,
} from "lucide-react";
import {
  instagramService,
  InstagramPost,
  InstagramAccountInfo,
} from "../services/instagramService";
import InstagramEmbed from "./InstagramEmbed";
import PlayfulLoader from "./PlayfulLoader";

interface InstagramFeedProps {
  posts?: InstagramPost[];
  useEmbedFallback?: boolean;
  showPosts?: boolean;
}

// Static Instagram post URLs for embed fallback
const STATIC_INSTAGRAM_POSTS = [
  "https://www.instagram.com/p/example1/",
  "https://www.instagram.com/p/example2/",
  "https://www.instagram.com/p/example3/",
  "https://www.instagram.com/p/example4/",
  "https://www.instagram.com/p/example5/",
  "https://www.instagram.com/p/example6/",
  "https://www.instagram.com/p/example7/",
  "https://www.instagram.com/p/example8/",
];

export const InstagramFeed: React.FC<InstagramFeedProps> = ({
  posts: propPosts,
  useEmbedFallback = true,
  showPosts = true,
}) => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [accountInfo, setAccountInfo] = useState<InstagramAccountInfo | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [useEmbeds, setUseEmbeds] = useState(false);

  useEffect(() => {
    const fetchInstagramData = async () => {
      try {
        setIsLoading(true);

        // Try to fetch real data first
        const [accountData, postsData] = await Promise.all([
          instagramService.getAccountInfo(),
          instagramService.getPosts(8),
        ]);

        // Check if we got real data or mock data
        const hasRealData =
          accountData && accountData.id !== "mock_instagram_id";

        if (hasRealData) {
          setAccountInfo(accountData);
          setPosts(postsData);
          setUseEmbeds(false);
        } else if (useEmbedFallback) {
          // Use embed fallback
          setUseEmbeds(true);
          setAccountInfo(instagramService.getMockAccountInfo());
        } else {
          // Use mock data
          setAccountInfo(accountData);
          setPosts(postsData);
          setUseEmbeds(false);
        }
      } catch (error) {
        console.error("Error fetching Instagram data:", error);
        if (useEmbedFallback) {
          setUseEmbeds(true);
          setAccountInfo(instagramService.getMockAccountInfo());
        } else if (propPosts) {
          setPosts(propPosts);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInstagramData();
  }, [propPosts, useEmbedFallback]);

  const handlePostClick = (post: InstagramPost) => {
    window.open(post.permalink, "_blank");
  };

  const handleFollowClick = () => {
    window.open("https://www.instagram.com/benipal_toys/", "_blank");
  };

  if (isLoading && showPosts) {
    return (
      <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <span className="text-4xl mr-2 animate-bounce">📷</span>
              <Instagram className="h-8 w-8 text-pink-500 mr-3 animate-pulse" />
              <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                Follow Our Journey
              </h2>
              <Instagram
                className="h-8 w-8 text-pink-500 ml-3 animate-pulse"
                style={{ animationDelay: "0.5s" }}
              />
              <span
                className="text-4xl ml-2 animate-bounce"
                style={{ animationDelay: "0.3s" }}
              >
                ✨
              </span>
            </div>
          </div>
          <div className="flex justify-center py-12">
            <PlayfulLoader
              size="lg"
              variant="rainbow"
              message="📸 Loading Instagram posts..."
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Instagram className="h-8 w-8 text-pink-500 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Follow Our Journey
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See our latest products, customer moments, and behind-the-scenes fun
            on Instagram!
          </p>
          <a
            href="https://www.instagram.com/benipal_toys/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center mt-4 text-pink-600 hover:text-pink-700 font-semibold transition-colors duration-200"
          >
            <Instagram className="h-5 w-5 mr-2" />@
            {accountInfo?.username || "benipal_toys"}
            <ExternalLink className="h-4 w-4 ml-1" />
          </a>
        </div>

        {/* Instagram Posts Grid - Only show if feature flag is enabled */}
        {showPosts && (
          <>
            {useEmbeds ? (
              // Use Instagram Embeds
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {STATIC_INSTAGRAM_POSTS.slice(0, 8).map((postUrl, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
                  >
                    <InstagramEmbed
                      postUrl={postUrl}
                      width={400}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            ) : (
              // Use API Data
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                  >
                    {/* Post Image */}
                    <div className="relative overflow-hidden">
                      <img
                        src={post.mediaUrl}
                        alt="Instagram post"
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                      />

                      {/* Instagram Icon Overlay */}
                      <div className="absolute top-3 right-3 bg-black bg-opacity-50 rounded-full p-2">
                        <Instagram className="h-4 w-4 text-white" />
                      </div>

                      {/* Media Type Badge */}
                      {post.mediaType === "VIDEO" && (
                        <div className="absolute top-3 left-3 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          📹 Video
                        </div>
                      )}

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <button
                          onClick={() => handlePostClick(post)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white text-gray-900 px-4 py-2 rounded-full font-semibold text-sm flex items-center"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Post
                        </button>
                      </div>
                    </div>

                    {/* Post Stats */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center text-gray-600">
                            <Heart className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">
                              {post.likeCount}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <MessageCircle className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">
                              {post.commentsCount}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center text-gray-500 text-xs">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(post.timestamp).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Caption Preview */}
                      <p className="text-sm text-gray-700 line-clamp-3">
                        {post.caption}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Call to Action */}
        <div className="text-center mt-12">
          <button
            onClick={handleFollowClick}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <Instagram className="h-5 w-5 mr-2" />
            Follow Us on Instagram
          </button>

          {/* Account Stats */}
          {accountInfo && (
            <div className="flex justify-center items-center mt-6 space-x-8 text-sm text-gray-600">
              <span>📸 {accountInfo.mediaCount} Posts</span>
              <span>
                👥 {accountInfo.followersCount.toLocaleString()} Followers
              </span>
              <span>⭐ 4.9/5 Rating</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
